import {
  AskConfirmation,
  ConfirmationButtonType,
  getConfirmationButtons
} from '../../../webmodule-common/other/ui/modal-confirmation';
import { cache } from '../../dealer-franchisee/cache-impl/cache-registry';
import { canJobJoinPurchaseOrder } from '../data/fabrication-job-transitions';
import { css, CSSResult, html, TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';
import { DataTableWrapper } from '../../../webmodule-common/other/ui/datatable-view';
import { emptyGuid } from '../../../webmodule-common/other/api/guid';
import { EventGetReference, EventSnippet } from '../../../webmodule-common/other/ui/events';
import { FabricationJobState, FabricationJobSummaryView } from '../../api/fabrication-job-api';
import {
  getAddToPurchaseOrderBadge,
  getPurchaseOrderBadgeLink
} from '../../dealer-franchisee/fabrication-purchase-orders/data/fabrication-purchase-order-helper-functions';
import { getApiFactory } from '../../api/api-injector';
import { getFabricationJobStatus } from '../data/fabrication-job-helper-functions';
import { goURL, resolveURL } from '../../../webmodule-common/other/ui/resource-resolver';
import { inCellBadgeLinkStyles } from '../../../webmodule-common/other/ui/in-cell-badge-link-styles';
import { IStaleData } from '../../../webmodule-common/other/stale-data';
import { LangFabricationJobSummaryTable } from '../../../localization/scripts/fabrication-jobs/views/fabrication-job-summary-table-lang';
import { LangFabricationPurchaseOrder } from '../../../localization/scripts/fabrication-purchase-orders/views/fabrication-purchase-order-lang';
import { offerAdvanceToAwaitingOrder } from '../ui/offer-advance-to-awaiting-order';
import { PurchaseOrderBadgeView } from '../../api/dealer-api-interface-all';
import { RequestPage, ResultPaginated } from '../../../webmodule-common/other/ui/RequestPage';
import { resourceFabricationJob } from '../ui/launcher';
import { addJobToPurchaseOrder as runAddJobToPurchaseOrder } from '../../fabrication-purchase-orders/ui/add-job-to-purchase-order';
import { staleIcon } from '../../../webmodule-common/other/ui/icons/icon-notification-signal';
import { userDataStore } from '../../dealer-franchisee/common/current-user-data-store';
import {
  WebmoduleSelectEvent,
  WebModuleTable,
  WebModuleTableColumnDef
} from '../../../webmodule-common/components/src/webmodule-components';

export type TableEventFabricationJob = (job: FabricationJobSummaryView) => Promise<void>;

export interface FabricationJobSummaryTableOptions {
  states: FabricationJobState[];
  title: EventSnippet;
  branchId: EventGetReference;
  pageFragment: string;
  deleteJobEvent?: TableEventFabricationJob;
  stale?: IStaleData;
}

/**
 * Styles injected into the <webmodule-table>'s shadow root via its `customStyles`
 * hook. The table renders cell content inside its shadow DOM, where the page
 * stylesheet cannot reach, so the badge cell's wrapping comes from here (successor
 * to the legacy `column-wrap` opt-in in simple_table.scss).
 */
const fabricationJobSummaryTableStyles = css`
  ${inCellBadgeLinkStyles}

  /* Purchase-order badges: one badge per linked PO, wrapping onto further lines and
     growing the row rather than scrolling sideways out of reach (the legacy
     column-wrap opt-in). */
  .wm-table-content .wm-table-cell.fabrication-job-purchase-orders {
    flex-wrap: wrap;
    align-content: center;
    row-gap: 2px;
  }
`;

@customElement('wm-fabricationjobsummarytable')
export class FabricationJobSummaryTable extends DataTableWrapper<FabricationJobSummaryView> {
  eventTitle: EventSnippet;
  branchId: EventGetReference;
  states: FabricationJobState[];
  searchTerm: string | null = null;
  pageFragment: string;
  dataIsStale = false;
  deleteJobEvent?: TableEventFabricationJob;
  staleData: IStaleData;
  private poBadges = new Map<string, PurchaseOrderBadgeView[]>();
  private userCache = cache().userProfile;

  constructor(options: FabricationJobSummaryTableOptions) {
    super();
    this.states = options.states;
    this.eventTitle = options.title;
    this.branchId = options.branchId;
    this.pageFragment = options.pageFragment;
    this.deleteJobEvent = options.deleteJobEvent;
    this.staleData = options.stale ?? { exists: () => false };
  }

  stale(row: FabricationJobSummaryView) {
    return this.staleData.exists(row.id);
  }

  isDataLoadDelayed(): boolean {
    return true;
  }

  override enableFiltering(): boolean {
    return true;
  }

  // The list tabs are a fixed-height region; let the table own the vertical scroll so a
  // second (tab) scrollbar does not appear beside it. The fill-height flex chain for the
  // wrapper is set up in tab-manager.styles.ts, keyed on the marker class DataTableWrapper
  // adds. Picker subclasses that render this table in a modal turn it back off.
  override fillHeight(): boolean {
    return true;
  }

  // The table renders cell content in its shadow DOM; carry the badge cell's
  // wrapping rule, which the page stylesheet can no longer reach.
  override customStyles(): CSSResult {
    return fabricationJobSummaryTableStyles;
  }

  override updateFilter(_searchTerm: string | null): void {
    this.searchTerm = _searchTerm;
  }

  async getRowsFromServer(request: RequestPage): Promise<ResultPaginated<FabricationJobSummaryView>> {
    const api = getApiFactory().fabricationJob();
    const result = await api.getFabricationJobSummary({
      branchId: await this.branchId(),
      states: this.states,
      pageIndex: request.pageIndex,
      pageSize: request.pageSize,
      searchTerm: this.searchTerm ?? null
    });

    if (!result) {
      return {
        count: 0,
        pageCount: 0,
        pageIndex: 0,
        pageSize: this.pageLength(),
        results: []
      };
    }

    await Promise.all([
      this.loadPoBadges(result.results),
      this.userCache.preFetch(result.results.map(x => x.assignedToUserId))
    ]);

    return result;
  }

  // Why fetch here rather than per-render? getRowsFromServer runs once per page load, so
  // the badges are cached and not re-fetched on every table re-render.
  private async loadPoBadges(jobs: FabricationJobSummaryView[]): Promise<void> {
    this.poBadges.clear();
    if (!userDataStore.isFabricatorMode) return;

    // A job is linked to POs only once it leaves Draft; don't fetch for Draft jobs.
    const eligible = jobs.filter(job => job.state !== FabricationJobState.Draft);
    if (eligible.length === 0) return;

    // One round-trip for the whole page rather than one request per job.
    const poApi = getApiFactory().fabricationPurchaseOrder();
    const grouped = await poApi.getPurchaseOrdersForJobs({ fabricationJobIds: eligible.map(job => job.id) });
    for (const group of grouped ?? []) {
      this.poBadges.set(group.fabricationJobId, group.badges);
    }
  }

  getColumns(): WebModuleTableColumnDef[] {
    // Column sizing moves from Bootstrap `colpx-*` / `colpxmax-*` classes (which
    // don't pierce the new table's shadow DOM) to the column's own width model.
    // A lone `minWidth` leaves the cell at `flex: 1 1 0`, so every column would grow
    // to an equal share; the short, bounded columns therefore carry a `maxWidth` too
    // and only the free-text Title column is left uncapped to absorb the slack.
    //
    // No column is `sortable`, deliberately: `RequestGetFabricationJobSummary` has no sort
    // field at all (branchId / states / pageIndex / pageSize / searchTerm), so
    // getRowsFromServer has nothing to forward, and the table does not sort client-side -
    // it only re-emits sortField on the fetch event. A sortable header here would re-fetch
    // and return the identical order. Making these lists sortable needs a sort parameter on
    // the summary endpoint first.
    return [
      {
        title: () => LangFabricationJobSummaryTable.jobNumberTitle(),
        fieldName: 'jobNumber',
        minWidth: '80px',
        maxWidth: '140px',
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const job = item as FabricationJobSummaryView;
          return this.stale(job)
            ? // eslint-disable-next-line @typescript-eslint/no-base-to-string
              html`${staleIcon()}${this.getJobLink(job, job.jobNumber.toString())}`
            : this.getJobLink(job, job.jobNumber.toString());
        }
      },
      {
        title: () => LangFabricationJobSummaryTable.titleTitle(),
        fieldName: 'title',
        minWidth: '150px',
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const job = item as FabricationJobSummaryView;
          return this.getJobLink(job, job.title || LangFabricationJobSummaryTable.untitled());
        }
      },
      {
        title: () => LangFabricationJobSummaryTable.statusTitle(),
        fieldName: 'state',
        minWidth: '90px',
        maxWidth: '180px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const job = item as FabricationJobSummaryView;
          return getFabricationJobStatus(job.state, true);
        }
      },
      {
        title: () => LangFabricationPurchaseOrder.onPurchaseOrdersColumn(),
        fieldName: 'purchaseOrders',
        minWidth: '120px',
        maxWidth: '220px',
        classes: 'fabrication-job-purchase-orders',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const job = item as FabricationJobSummaryView;
          const badges = this.poBadges.get(job.id) ?? [];
          // An empty cell is the one place the next action is obvious, so offer it here rather than
          // making the user find it in the row menu. Same gate as that menu item, so the two never
          // disagree about whether the job can go on an order.
          if (badges.length === 0)
            return this.canAddToPurchaseOrder(job)
              ? getAddToPurchaseOrderBadge(() => void this.addJobToPurchaseOrder(job))
              : html``;
          return html`${badges.map(po => getPurchaseOrderBadgeLink(po))}`;
        }
      },
      {
        title: () => LangFabricationJobSummaryTable.itemsTitle(),
        fieldName: 'itemCount',
        minWidth: '70px',
        maxWidth: '110px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const job = item as FabricationJobSummaryView;
          return html`${job.itemCount}`;
        }
      },
      {
        title: () => LangFabricationJobSummaryTable.authorTitle(),
        fieldName: 'assignedToUserId',
        minWidth: '120px',
        maxWidth: '200px',
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const job = item as FabricationJobSummaryView;
          return this.getUserDisplayValue(job.assignedToUserId);
        }
      },
      {
        title: () => LangFabricationJobSummaryTable.createdTitle(),
        fieldName: 'dateCreated',
        minWidth: '110px',
        maxWidth: '140px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const job = item as FabricationJobSummaryView;
          const dt = new Date(job.dateCreated);
          return html`${dt.toLocaleDateString()}`;
        }
      },
      {
        title: () => LangFabricationJobSummaryTable.modifiedTitle(),
        fieldName: 'lastModifiedDate',
        minWidth: '110px',
        maxWidth: '140px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const job = item as FabricationJobSummaryView;
          if (!job.lastModifiedDate) return html``;
          const dt = new Date(job.lastModifiedDate);
          return html`${dt.toLocaleDateString()}`;
        }
      },
      {
        title: () => '',
        width: '64px',
        fieldName: 'xx',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const job = item as FabricationJobSummaryView;
          return this.ellipsisMenu(job);
        }
      }
    ];
  }

  getDefaultSortAsc(): boolean {
    return false;
  }

  getDefaultSortField(): string {
    return 'jobNumber';
  }

  protected getUserDisplayValue(value: string): TemplateResult {
    if (value === emptyGuid) return html``;
    //prefetch should already have been called
    return html`${this.userCache.getLocal(value)?.displayValue ?? ''}`;
  }

  private jobLinkClicked(e: MouseEvent, jobId: string) {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    goURL(resourceFabricationJob, jobId);
  }

  protected getJobLink(row: FabricationJobSummaryView, value: string): TemplateResult {
    return html`<a
      class="fabrication-job-link"
      href="${resolveURL(resourceFabricationJob, row.id)}"
      data-fabricationjobid="${row.id}"
      @click=${(e: MouseEvent) => this.jobLinkClicked(e, row.id)}
      >${value}</a
    >`;
  }

  private async onMenuItemSelected(event: WebmoduleSelectEvent, job: FabricationJobSummaryView) {
    if (event.detail.item.value === 'delete') {
      if (
        await AskConfirmation(
          LangFabricationJobSummaryTable.deleteConfirmation(),
          getConfirmationButtons()[ConfirmationButtonType.yesNo]
        )
      ) {
        await this.deleteJobEvent?.(job);
      }
    } else if (event.detail.item.value === 'add-to-po') {
      await this.addJobToPurchaseOrder(job);
    }
  }

  /**
   * A job can join a purchase order once it is past Draft but before production starts - the
   * window in which its materials still need ordering.
   */
  private canAddToPurchaseOrder(job: FabricationJobSummaryView): boolean {
    return canJobJoinPurchaseOrder(job.state) && userDataStore.isFabricatorMode;
  }

  private async addJobToPurchaseOrder(job: FabricationJobSummaryView): Promise<void> {
    if (!(await runAddJobToPurchaseOrder(await this.branchId(), job.id))) return;
    // A Planned job that now has an order is really waiting on it, so offer the state change here
    // rather than making the user go and find it. Declines itself for any other state.
    await offerAdvanceToAwaitingOrder(job);
    await this.refreshData();
  }

  private ellipsisMenu(job: FabricationJobSummaryView): TemplateResult {
    const isDraft = job.state === FabricationJobState.Draft;
    const showAddToPo = this.canAddToPurchaseOrder(job);

    if (!isDraft && !showAddToPo) return html``;

    return html`<webmodule-dropdown
      placement="bottom-end"
      hoist
      @webmodule-select=${(e: WebmoduleSelectEvent) => this.onMenuItemSelected(e, job)}
    >
      <webmodule-icon-button slot="trigger" library="fa" name="fas-bars"></webmodule-icon-button>
      <webmodule-menu>
        ${isDraft
          ? html`<webmodule-menu-item value="delete">
              <webmodule-icon slot="prefix" library="fa" name="fas-trash"></webmodule-icon>
              ${LangFabricationJobSummaryTable.deleteMenuItem()}
            </webmodule-menu-item>`
          : html``}
        ${showAddToPo
          ? html`<webmodule-menu-item value="add-to-po">
              <webmodule-icon slot="prefix" library="fa" name="fas-cart-plus"></webmodule-icon>
              ${LangFabricationPurchaseOrder.addToPoMenu()}
            </webmodule-menu-item>`
          : html``}
      </webmodule-menu>
    </webmodule-dropdown>`;
  }
}
